import { useState, useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { Search, Plus, X, UserCheck, UserX, Pencil, MapPin, Check, Warehouse, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';

import { PasswordInput } from '@/components/ui/password-input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useUsers, useCreateUser, useUpdateUser } from '@/hooks/use-users';
import { useTenantRoles } from '@/hooks/use-tenant-roles';
import { useAuthStore } from '@/stores/auth-store';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { formatDateTime } from '@/lib/utils';
import { isWarehouseType } from '@/lib/location-type';
import { ROLE_LEVEL, ROLE_PERMISSIONS, expandPermissions, roleLabel } from '@supernova/shared';
import { usePermissions } from '@/hooks/use-permissions';
import { RolesPanel } from './roles-panel';
import { UserPermissionsField } from './user-permissions-field';

const inputBase = 'h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

// ── Password Policy Rules (same as signup form) ──
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One numeric digit', test: (p: string) => /\d/.test(p) },
  { key: 'special', label: 'One special character (!@#$%...)', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

function PasswordChecklist({ password }: { password: string }) {
  if (!password) return null;
  return (
    <ul className="mt-2 space-y-1">
      {PASSWORD_RULES.map((rule) => {
        const passed = rule.test(password);
        return (
          <li key={rule.key} className="flex items-center gap-1.5 text-xs">
            {passed ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3 text-red-400" />}
            <span className={passed ? 'text-green-600' : 'text-muted-foreground'}>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// `employee` is the stored value for Cashier, kept so existing users and POS
// PIN logins are unaffected.
const LINE_ROLE_OPTIONS = [
  { value: 'employee', label: 'Cashier' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
];
const ALL_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  ...LINE_ROLE_OPTIONS,
];

type StatusTab = 'active' | 'inactive';

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<StatusTab>('active');
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('employee');
  const [formLocationIds, setFormLocationIds] = useState<string[]>([]);
  const [formExtras, setFormExtras] = useState<string[]>([]);
  const [formCustomRoleId, setFormCustomRoleId] = useState('');
  const [showRoles, setShowRoles] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDialog, setConfirmDialog] = useState<{ userId: string; userName: string } | null>(null);

  const { user } = useAuthStore();
  const permissions = usePermissions();
  // Owners and admins manage everyone. Anyone else here holds Users permissions
  // and manages only staff below their own level (enforced in auth too).
  const myLevel = ROLE_LEVEL[user?.role ?? ''] ?? 0;
  const manageAll = myLevel >= ROLE_LEVEL.admin;
  const canCreate = manageAll || permissions.has('users.create');
  const canEdit = manageAll || permissions.has('users.edit');
  const canDeactivate = manageAll || permissions.has('users.deactivate');
  // Only owners and admins may grant extra permissions (enforced in auth too).
  const canGrant = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'super_admin';
  const { data: rolesData } = useTenantRoles({ enabled: canGrant && user?.role !== 'super_admin' });
  const customRoles = rolesData?.data ?? [];
  const selectedCustomRole = customRoles.find((r) => r.id === formCustomRoleId) ?? null;
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocations();
  const locations = locationsData?.data ?? [];

  // Below Admin, a user can be managed when they rank lower, or rank the same and
  // hold nothing the manager lacks (auth decides the same way).
  const canActOn = (row: any) => {
    if (manageAll) return true;
    if (row.id === user?.id) return false;
    const level = ROLE_LEVEL[row.role] ?? Infinity;
    if (level > myLevel) return false;
    if (level < myLevel) return true;
    if (row.customRoleId) return false;
    return expandPermissions([...(ROLE_PERMISSIONS[row.role] ?? []), ...(row.extraPermissions ?? [])]).every((k) =>
      permissions.has(k),
    );
  };
  // Anyone else may give a built-in role up to Manager and their own level, with no more access than they have.
  const roleOptions = manageAll
    ? ALL_ROLE_OPTIONS
    : ALL_ROLE_OPTIONS.filter(
        (o) =>
          o.value !== 'admin' &&
          ROLE_LEVEL[o.value] <= myLevel &&
          ROLE_PERMISSIONS[o.value].every((k) => permissions.has(k)),
      );

  // Fetch all users from API (large perPage to get all for client-side filtering)
  const { data, isLoading, isError, refetch } = useUsers({
    page: 1,
    perPage: 500,
    search: search || undefined,
    locationId: selectedLocationId || undefined,
  });

  // Only fetch all users (unfiltered) for duplicate email detection when create form is open
  const { data: allUsersData } = useUsers({ page: 1, perPage: 500 }, { enabled: showForm });

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const allUsers = data?.data ?? [];

  // Client-side filtering by active/inactive tab
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u: any) => (activeTab === 'active' ? u.isActive : !u.isActive));
  }, [allUsers, activeTab]);

  // Client-side pagination
  const totalFiltered = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [filteredUsers, page, perPage]);

  // Count for tab badges
  const activeCount = useMemo(() => allUsers.filter((u: any) => u.isActive).length, [allUsers]);
  const inactiveCount = useMemo(() => allUsers.filter((u: any) => !u.isActive).length, [allUsers]);

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('employee');
    setFormLocationIds([]);
    setFormExtras([]);
    setFormCustomRoleId('');
    setFormError('');
    setFieldErrors({});
    setShowForm(false);
    setShowEditModal(false);
    setEditUserId(null);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formName.trim()) errors.name = 'Name is required';
    else if (!/^[A-Za-z\s]+$/.test(formName)) errors.name = 'Only alphabet characters and spaces are allowed';
    if (!editUserId) {
      if (!formEmail.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) errors.email = 'Please enter a valid email';
      if (!formPassword) errors.password = 'Password is required';
      else if (!PASSWORD_RULES.every((rule) => rule.test(formPassword)))
        errors.password = 'Password does not meet all requirements';
    }
    if (locations.length > 0 && formLocationIds.length === 0) {
      errors.locations = 'Please select at least one location';
    }
    return errors;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Check for duplicate email in existing users (active + inactive)
    const existingAllUsers = allUsersData?.data ?? [];
    const existingUser = existingAllUsers.find((u: any) => u.email?.toLowerCase() === formEmail.trim().toLowerCase());

    if (existingUser) {
      if (existingUser.isActive) {
        setFormError('This user already exists.');
      } else {
        setFormError(
          'This user already exists and is currently inactive. Please reactivate the existing account instead of creating a new one.',
        );
      }
      return;
    }

    try {
      await createUser.mutateAsync({
        email: formEmail,
        name: formName,
        password: formPassword,
        role: formRole,
        locationIds: formLocationIds,
        ...(canGrant ? { extraPermissions: formExtras, customRoleId: formCustomRoleId || null } : {}),
      });
      resetForm();
    } catch (err: any) {
      setFormError(err?.error?.message || 'Failed to create user');
    }
  };

  const handleEditClick = (userRow: any) => {
    setEditUserId(userRow.id);
    setFormName(userRow.name || '');
    setFormEmail(userRow.email || '');
    setFormPassword('');
    setFormRole(userRow.role || 'employee');
    setFormLocationIds(userRow.locationIds || []);
    setFormExtras(userRow.extraPermissions || []);
    setFormCustomRoleId(userRow.customRoleId || '');
    setFormError('');
    setFieldErrors({});
    setShowEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await updateUser.mutateAsync({
        id: editUserId!,
        name: formName.trim(),
        role: formRole,
        locationIds: formLocationIds,
        ...(canGrant ? { extraPermissions: formExtras, customRoleId: formCustomRoleId || null } : {}),
      });
      resetForm();
    } catch (err: any) {
      setFormError(err?.error?.message || 'Failed to update user');
    }
  };

  const handleToggleActive = async (userId: string, currentActive: boolean, userName: string) => {
    if (currentActive) {
      // Show confirmation dialog before deactivating
      setConfirmDialog({ userId, userName });
    } else {
      // Activate immediately without confirmation
      await updateUser.mutateAsync({ id: userId, isActive: true });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmDialog) return;
    await updateUser.mutateAsync({ id: confirmDialog.userId, isActive: false });
    setConfirmDialog(null);
  };

  const handleTabChange = (tab: StatusTab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // ESC key + body scroll lock for edit modal
  useEffect(() => {
    if (!showEditModal) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetForm();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [showEditModal]);

  // Custom roles share the role picker with the built-in ones. Their base level
  // goes into formRole, so everything that reads the role keeps working.
  const roleSelectValue = formCustomRoleId ? `custom:${formCustomRoleId}` : formRole;
  // A manager can't load custom roles, so a user's own one is named from the row
  // and its permissions aren't shown.
  const unloadedCustomRole =
    formCustomRoleId && !selectedCustomRole
      ? (allUsers.find((u: any) => u.id === editUserId)?.customRoleName ?? 'Custom role')
      : null;
  const pickRole = (value: string) => {
    const custom = value.startsWith('custom:') ? customRoles.find((r) => `custom:${r.id}` === value) : undefined;
    const role = custom ? custom.baseRole : value;
    setFormCustomRoleId(custom ? custom.id : '');
    setFormRole(role);
    if (role === 'admin') setFormLocationIds(locations.map((loc: any) => loc.id));
  };
  const customRoleOptions = customRoles.length > 0 && (
    <optgroup label="Custom roles">
      {customRoles.map((role) => (
        <option key={role.id} value={`custom:${role.id}`}>
          {role.name}
        </option>
      ))}
    </optgroup>
  );

  const columns: ColumnDef<any>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'email', header: 'Email' },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => {
        const extras: string[] = row.original.extraPermissions || [];
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {row.original.customRoleName || roleLabel(row.original.role)}
            {extras.length > 0 && (
              <span
                className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                title="Extra permissions on top of the role"
              >
                +{extras.length} extra
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'locationIds',
      header: 'Locations',
      cell: ({ row }) => {
        const locIds: string[] = row.original.locationIds || [];
        if (locIds.length === 0) {
          return <span className="text-xs text-muted-foreground">All locations</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {locIds.map((locId: string) => {
              const loc = locations.find((l: any) => l.id === locId);
              const isWarehouse = isWarehouseType(loc?.type);
              return (
                <span
                  key={locId}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isWarehouse ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'
                  }`}
                >
                  {isWarehouse ? <Warehouse className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                  {loc?.nickname || loc?.name || locId.slice(0, 8)}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => <span className="whitespace-nowrap">{formatDateTime(row.original.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      cell: ({ row }) => {
        if (!canActOn(row.original) || !(canEdit || canDeactivate)) return null;

        const isSelf = user?.id === row.original.id;

        // Logged-in user cannot deactivate their own account
        if (isSelf && row.original.isActive) {
          return (
            <div className="flex gap-1">
              <button
                onClick={() => handleEditClick(row.original)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <span
                title="You cannot deactivate your own account"
                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md bg-red-300 px-2.5 py-1 text-xs font-medium text-white"
              >
                <UserX className="h-3.5 w-3.5" /> Deactivate
              </span>
            </div>
          );
        }

        return (
          <div className="flex gap-1">
            {canEdit && (
              <button
                onClick={() => handleEditClick(row.original)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            {canDeactivate && (
              <button
                onClick={() => handleToggleActive(row.original.id, row.original.isActive, row.original.name)}
                className={
                  row.original.isActive
                    ? 'inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700'
                    : 'inline-flex items-center gap-1.5 rounded-md bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700'
                }
              >
                {row.original.isActive ? (
                  <>
                    <UserX className="h-3.5 w-3.5" /> Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="h-3.5 w-3.5" /> Activate
                  </>
                )}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({ data: paginatedUsers, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="space-y-4">
      <PageHeader title="Users" description="Manage team members and their roles">
        <LocationBadge />
        {canGrant && user?.role !== 'super_admin' && (
          <button
            onClick={() => setShowRoles((open) => !open)}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <ShieldCheck className="h-4 w-4" />
            Roles
          </button>
        )}
        {canCreate && roleOptions.length > 0 && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
        )}
      </PageHeader>

      {showRoles && <RolesPanel onClose={() => setShowRoles(false)} />}

      {/* Create User Form — inline, unchanged */}
      {showForm && (
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Add New User</h3>
            <button onClick={resetForm} className="rounded-md p-1 hover:bg-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
          {formError && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{formError}</div>}
          <form onSubmit={handleCreate} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                  setFormName(value);
                  setFieldErrors((prev) => ({ ...prev, name: '' }));
                }}
                className={fieldErrors.name ? inputError : inputNormal}
                placeholder="Jane Doe"
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <input
                type="email"
                value={formEmail}
                onChange={(e) => {
                  setFormEmail(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, email: '' }));
                }}
                className={fieldErrors.email ? inputError : inputNormal}
                placeholder="jane@company.com"
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Password</label>
              <PasswordInput
                value={formPassword}
                onChange={(e) => {
                  setFormPassword(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, password: '' }));
                }}
                className={fieldErrors.password ? inputError : inputNormal}
                placeholder="Enter password"
              />
              {fieldErrors.password && <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
              <PasswordChecklist password={formPassword} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Role</label>
              <select
                value={roleSelectValue}
                onChange={(e) => pickRole(e.target.value)}
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                {customRoleOptions}
              </select>
            </div>
            {locations.length > 0 && (
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="mb-1 block text-sm font-medium">
                  Locations <span className="text-red-500">*</span>
                </label>
                <div
                  className={`flex flex-wrap gap-3 rounded-lg border bg-background p-3 ${fieldErrors.locations ? 'border-red-500' : 'border-input'}`}
                >
                  {locations.map((loc: any) => (
                    <label key={loc.id} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formLocationIds.includes(loc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormLocationIds((prev) => [...prev, loc.id]);
                          } else {
                            setFormLocationIds((prev) => prev.filter((id) => id !== loc.id));
                          }
                          setFieldErrors((prev) => ({ ...prev, locations: '' }));
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      {loc.nickname ? `${loc.name} (${loc.nickname})` : loc.name}
                    </label>
                  ))}
                </div>
                {fieldErrors.locations && <p className="mt-1 text-xs text-red-500">{fieldErrors.locations}</p>}
              </div>
            )}
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="mb-1 block text-sm font-medium">Permissions</label>
              <UserPermissionsField
                role={formRole}
                customRole={selectedCustomRole}
                extras={formExtras}
                onChange={setFormExtras}
                canGrant={canGrant}
              />
            </div>
            <div className="flex items-end sm:col-span-2 lg:col-span-4">
              <button
                type="submit"
                disabled={createUser.isPending}
                className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {createUser.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit User Modal Popup */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Dimmed backdrop — click outside to close */}
          <div className="absolute inset-0 bg-black/50" onClick={resetForm} />

          {/* Modal panel */}
          {/* Wide enough for the permission grid's View/Create/Edit/Delete columns. */}
          <div className="relative z-10 w-full max-w-3xl rounded-xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h3 className="text-lg font-semibold">Edit User</h3>
              <button onClick={resetForm} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto px-6 py-5">
              {formError && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{formError}</div>}
              <form onSubmit={handleEdit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                      setFormName(value);
                      setFieldErrors((prev) => ({ ...prev, name: '' }));
                    }}
                    className={fieldErrors.name ? inputError : inputNormal}
                    placeholder="Jane Doe"
                  />
                  {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
                </div>

                {/* Role */}
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Role</label>
                  <select
                    value={roleSelectValue}
                    disabled={formRole === 'owner'}
                    onChange={(e) => pickRole(e.target.value)}
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm disabled:opacity-70"
                  >
                    {/* The owner role can't be changed, so it is shown but not offered. */}
                    {formRole === 'owner' && <option value="owner">Owner</option>}
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    {customRoleOptions}
                    {unloadedCustomRole && <option value={roleSelectValue}>{unloadedCustomRole}</option>}
                  </select>
                </div>

                {/* Locations */}
                {locations.length > 0 && (
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium">
                      Locations <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`flex flex-wrap gap-3 rounded-lg border bg-background p-3 ${fieldErrors.locations ? 'border-red-500' : 'border-input'}`}
                    >
                      {locations.map((loc: any) => (
                        <label key={loc.id} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formLocationIds.includes(loc.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormLocationIds((prev) => [...prev, loc.id]);
                              } else {
                                setFormLocationIds((prev) => prev.filter((id) => id !== loc.id));
                              }
                              setFieldErrors((prev) => ({ ...prev, locations: '' }));
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          {loc.nickname ? `${loc.name} (${loc.nickname})` : loc.name}
                        </label>
                      ))}
                    </div>
                    {fieldErrors.locations && <p className="mt-1 text-xs text-red-500">{fieldErrors.locations}</p>}
                  </div>
                )}

                {/* Permissions */}
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Permissions</label>
                  {unloadedCustomRole ? (
                    <div className="rounded-lg border border-input bg-background p-3 text-sm text-muted-foreground">
                      {unloadedCustomRole} is a custom role. Owners and admins set its permissions under Roles.
                    </div>
                  ) : (
                    <UserPermissionsField
                      role={formRole}
                      customRole={selectedCustomRole}
                      extras={formExtras}
                      onChange={setFormExtras}
                      canGrant={canGrant}
                    />
                  )}
                </div>

                {/* Footer actions inside form so submit works */}
                <div className="flex justify-end gap-3 sm:col-span-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={updateUser.isPending}
                    className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateUser.isPending}
                    className="inline-flex h-9 items-center rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Active / Inactive Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => handleTabChange('active')}
          className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'active' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="flex items-center gap-2">
            Active
            <span
              className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                activeTab === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {activeCount}
            </span>
          </span>
          {activeTab === 'active' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
        </button>
        <button
          onClick={() => handleTabChange('inactive')}
          className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'inactive' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="flex items-center gap-2">
            Inactive
            <span
              className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                activeTab === 'inactive' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {inactiveCount}
            </span>
          </span>
          {activeTab === 'inactive' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : isError ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
          <p className="text-sm">Failed to load users. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <DataTable table={table} columns={columns} />
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={totalFiltered}
            perPage={perPage}
            onPageChange={setPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={handlePerPageChange}
          />
        </>
      )}

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title="Deactivate User"
        message={`Are you sure you want to deactivate "${confirmDialog?.userName}"? This user will no longer be able to access the system.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={updateUser.isPending}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
